import React from 'react'
import firebase from 'firebase/app'
import moment from 'moment'
import { slice, mean, capitalize, sumBy } from 'lodash'
import styled from 'styled-components'
import { Segment, Header, Button, Table, Pagination, Label, Popup } from 'semantic-ui-react'

import RealTimeTracker from '../../../components/common/tracker'
import DateRangeSearch from './DateRangeSearch'

const NUMBER_PER_PAGE = 50

const DurationFromNow = styled.span`
  color: rgba(208, 62, 62, 1);
`

class OrderTab extends React.Component {
  state = {
    from: moment().startOf('day').format('x'),
    to: moment().endOf('day').format('x'),

    orders: [],
    successOrders: [],
    currentPage: 1,
    numberOfPages: 1,
    loading: false,

    deliveryTookAvg: 0,
  }

  componentDidMount() {
    this.subscription = null
    this.getOrders()
  }

  componentWillUnmount() {
    if (this.subscription) this.subscription.off()
  }

  onSearchOrder = () => {
    const { from, to } = this.state

    if (from === '' || to === '') {
      // eslint-disable-next-line no-alert
      alert('Please select date')
      return;
    }

    if (this.subscribe) {
      this.subscribe.off()
    }

    this.getOrders()
  }

  getAverageDelivered = () => {
    const { successOrders } = this.state

    const orders = successOrders.map(order => {
      const startAt = moment(parseInt(order.created_at, 10))
      const endAt = moment(parseInt(order.updated_at, 10))
      const durationFromNowToStartAt = moment.duration(startAt.diff(endAt)).as('minutes')

      const positiveDurationFromNowToStartAt = Math.abs(parseInt(durationFromNowToStartAt, 10))
      return positiveDurationFromNowToStartAt
    });

    const deliveryTookAvg = parseInt(mean(orders), 10) || 0

    this.setState({ deliveryTookAvg })
  }

  getPageSize = () => {
    const { orders } = this.state
    const numberOfPages = Math.ceil(orders.length / NUMBER_PER_PAGE)

    this.setState({ numberOfPages })
  }

  getOrders = () => {
    const { driverUid } = this.props
    const { from, to } = this.state

    this.setState({ loading: true })

    this.subscription = firebase
      .database()
      .ref(`deliverer_orders/${driverUid}`)
      .orderByChild("created_at")
      .startAt(parseInt(from, 10))
      .endAt(parseInt(to, 10))

    this.subscription.on('value', snapshot => {
      const successOrders = this.sortOrders(snapshot, 'delivered')
      const allOrders = this.sortOrders(snapshot)

      this.setState({ successOrders, orders: allOrders, loading: false }, () => {
        this.getAverageDelivered()
        this.getPageSize()
      })
    })
  }

  getOrderStatus = status => {
    switch (status) {
      case 'pending': return {
        label: 'Pending',
        color: 'yellow'
      }
      case 'confirmed': return {
        label: 'Confirmed',
        color: 'yellow'
      }
      case 'outForDelivery': return {
        label: 'Out for delivery',
        color: 'yellow'
      }
      case 'rejected': return {
        label: 'Rejected',
        color: 'red'
      }
      case 'delivered': return {
        label: 'Delivered',
        color: 'green'
      }
      default: return {
        label: capitalize(status.replace(/_/g, " ")),
        color: 'orange'
      }
    }
  }

  handlePaginationChange = (e, { activePage }) => this.setState({ currentPage: activePage })

  sortOrders = (orders, status) => {
    const sortedOrders = []

    orders.forEach(snapshot => {
      const order = snapshot.val()
      const { key } = snapshot

      if (!status || status === order.status) {
        sortedOrders.push({ ...order, key })
      }
    })

    return sortedOrders
  }

  getAllDeliveryFee = () => {
    const { successOrders } = this.state

    const allDeliveryFee = sumBy(successOrders, 'delivery_fee')

    return parseFloat(allDeliveryFee).toFixed(2)
  }

  getIncomeFromRestaurants = () => {
    const { successOrders } = this.state

    const allIncome = sumBy(successOrders, order => Number(order.sub_total) * Number(order.discount_rate) / 100)

    return parseFloat(allIncome).toFixed(2)
  }

  getTotalActualReturn = () => {
    const { successOrders } = this.state

    const allDeliveryFee = this.getAllDeliveryFee(successOrders)
    const allIncome = this.getIncomeFromRestaurants(successOrders)
    const actualReturn = Number(allDeliveryFee) + Number(allIncome)

    return parseFloat(actualReturn).toFixed(2)
  }

  renderFooter = () => {
    const { currentPage, numberOfPages, orders } = this.state

    if (orders.length === 0) return null

    return (
      <Table.Row>
        <Table.HeaderCell colSpan='8'>
          <Pagination
            activePage={currentPage}
            onPageChange={this.handlePaginationChange}
            totalPages={numberOfPages}
            floated='right'
          />
        </Table.HeaderCell>
      </Table.Row>
    )
  }

  renderStatusLabel = status => {
    const { color, label } = this.getOrderStatus(status)

    return (
      <Label
        color={color}
        style={{ margin: 0, marginBottom: 5, width: 120 }}
      >
        {label}
      </Label>
    )
  }

  renderRejectedLabel = order => {
    const { color, label } = this.getOrderStatus(order.status)

    return (
      <Popup
        position="top center"
        wide
        trigger={
          <Label
            color={color}
            style={{ margin: 0, marginBottom: 5, width: 120 }}
          >
            {label}
          </Label>
        }
      >
        {order.rejected_reason || 'No description'}
      </Popup>
    )
  }

  renderRow = (order, index) => {
    const { onViewOrderDetail } = this.props

    const createdAt = {
      fromNow: moment(order.created_at).fromNow(),
      date: moment(order.created_at).format('DD-MMM-YYYY'),
      time: moment(order.created_at).format('HH:mm a'),
    }

    const updatedAt = {
      fromNow: moment(order.updated_at).fromNow(),
      date: moment(order.updated_at).format('DD-MMM-YYYY'),
      time: moment(order.updated_at).format('HH:mm a'),
    }

    return (
      <Table.Row key={index}>
        <Table.Cell textAlign="center">
          <Label>{order.order_number.toUpperCase()}</Label>
        </Table.Cell>
        <Table.Cell textAlign="center">
          <Label>$ {order.sub_total}</Label>
        </Table.Cell>
        <Table.Cell textAlign="center">
          <Label>$ {order.delivery_fee}</Label>
        </Table.Cell>
        <Table.Cell textAlign="center">
          <DurationFromNow>{createdAt.fromNow}</DurationFromNow>
          <div>{createdAt.time}</div>
          <div>{createdAt.date}</div>
        </Table.Cell>
        <Table.Cell textAlign="center">
          <DurationFromNow>{updatedAt.fromNow}</DurationFromNow>
          <div>{updatedAt.time}</div>
          <div>{updatedAt.date}</div>
          <br />
        </Table.Cell>
        <Table.Cell textAlign="center">
          {
            order.status === 'rejected'
              ? this.renderRejectedLabel(order)
              : this.renderStatusLabel(order.status)
          }
          <br />
          <Button
            onClick={() => onViewOrderDetail(order)}
            size="mini"
            style={{ margin: 0, width: 120 }}
          >
            View Detail
          </Button>
        </Table.Cell>
      </Table.Row>
    )
  }

  renderCurrentPageOrders = () => {
    const { orders, currentPage } = this.state
    let orderRows = []

    const begin = ((currentPage - 1) * NUMBER_PER_PAGE)
    const end = begin + NUMBER_PER_PAGE

    if (orders.length > 0) {
      const pageList = slice(orders, begin, end)

      orderRows = pageList.map(this.renderRow)
    }

    return orderRows.length > 0 ? orderRows : null
  }

  renderNoResult = () => (
    <Table.Row>
      <Table.Cell colSpan={6} textAlign="center">
        No results
      </Table.Cell>
    </Table.Row>
  )

  render() {
    const { from, to, successOrders, deliveryTookAvg, loading } = this.state

    const startFrom = moment(parseInt(from, 10)).format("YYYY-MM-DD")
    const endAt = moment(parseInt(to, 10)).format("YYYY-MM-DD")

    return (
      <div>
        <div className='ui equal width grid'>
          <div className='column'>
            <RealTimeTracker
              label="Orders"
              icon="shop"
              loaded
              actualValue={successOrders.length}
            />
          </div>
          <div className='column'>
            <RealTimeTracker
              label="AVG Duration per Delivery"
              icon="clock"
              actualValue={`${deliveryTookAvg} minutes`}
              loaded
              iconStyle={{ flex: 0.5 }}
            />
          </div>
        </div>
        <div className='ui equal width grid' style={{ marginTop: 0 }}>
          <div className='column'>
            <RealTimeTracker
              label="All Delivery Fee"
              icon="money"
              actualValue={`$ ${this.getAllDeliveryFee()}`}
              loaded
            />
          </div>
          <div className='column'>
            <RealTimeTracker
              label="Commission"
              icon="money"
              actualValue={`$ ${this.getIncomeFromRestaurants()}`}
              loaded
            />
          </div>
          <div className='column'>
            <RealTimeTracker
              label="Actual Return"
              icon="money"
              actualValue={`$ ${this.getTotalActualReturn()}`}
              loaded
            />
          </div>
        </div>

        <Segment color='black' style={{ marginBottom: 20 }}>
          <Header size='large'>Driver Orders</Header>
          <Segment>
            <DateRangeSearch
              start={startFrom}
              end={endAt}
              loading={loading}
              onButtonClick={this.onSearchOrder}
              onStartChange={value => this.setState({ from: value })}
              onEndChange={value => this.setState({ to: value })}
            />
          </Segment>
          <Table basic='very'>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell textAlign="center">Order Number</Table.HeaderCell>
                <Table.HeaderCell textAlign="center">SubTotal</Table.HeaderCell>
                <Table.HeaderCell textAlign="center">Delivery Fee</Table.HeaderCell>
                <Table.HeaderCell textAlign="center">Ordered at</Table.HeaderCell>
                <Table.HeaderCell textAlign="center">Updated at</Table.HeaderCell>
                <Table.HeaderCell textAlign="center">Actions</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              { this.renderCurrentPageOrders() || this.renderNoResult() }
              {this.renderFooter()}
            </Table.Body>
          </Table>
        </Segment>
      </div>
    )
  }
}

export default OrderTab



// WEBPACK FOOTER //
// ./src/containers/drivers/detail/OrderTab.js